import { useState, useCallback, useRef } from 'react';
import { EscortProfile } from '@/types';
import { generateProfileImage } from '@/services/replicateService';
import { saveProfileImageToDatabase } from '@/services/profileImage';
import { useToast } from '@/hooks/use-toast';

// Interface for the hook's return value
interface UseProfileImageGeneratorReturn {
  isGenerating: Map<string, boolean>;
  errors: Map<string, string>;
  generateImageForProfile: (profile: EscortProfile, showToast?: boolean) => Promise<string | void>;
  generateBatchImages: (profileBatch: EscortProfile[]) => Promise<void>;
  generatedImagesCache: React.MutableRefObject<Map<string, string>>;
}

export const useProfileImageGenerator = (): UseProfileImageGeneratorReturn => {
  const [isGenerating, setIsGenerating] = useState<Map<string, boolean>>(new Map());
  const [errors, setErrors] = useState<Map<string, string>>(new Map());
  const generatedImagesCache = useRef<Map<string, string>>(new Map()); // Cache specific to generated images
  const { toast } = useToast();

  const generateImageForProfile = useCallback(async (profile: EscortProfile, showToast: boolean = true): Promise<string | void> => {
    // If already generating, don't regenerate
    if (isGenerating.get(profile.id)) {
      if (showToast) {
        toast({
          title: 'Génération en cours',
          description: `Veuillez patienter, nous générons déjà une image pour ${profile.displayName}`,
        });
      }
      return;
    }

    // Clear any previous errors
    setErrors(current => {
      const updated = new Map(current);
      updated.delete(profile.id);
      return updated;
    });

    // Check if image is already in cache
    if (generatedImagesCache.current.has(profile.id)) {
      const cachedUrl = generatedImagesCache.current.get(profile.id)!;
      if (showToast) {
        toast({
          title: 'Image déjà générée',
          description: `Utilisation de l'image en cache pour ${profile.displayName}`,
        });
      }
      // Note: This hook doesn't manage the display state (profileImages),
      // it just returns the generated/cached URL. The caller should update its state.
      return cachedUrl;
    }

    // Set generating state
    setIsGenerating(current => new Map([...current, [profile.id, true]]));

    if (showToast) {
      toast({
        title: 'Génération d\'image',
        description: `Démarrage de la génération d'image pour ${profile.displayName}...`,
      });
    }

    try {
      console.log(`📸 Génération d'image pour le profil ${profile.id} (${profile.displayName})`);

      const imageUrl = await generateProfileImage({
        gender: profile.attributes.gender || 'person',
        ethnicity: profile.attributes.ethnicity,
        age: profile.age,
        displayName: profile.displayName
      });

      console.log(`✅ Image générée avec succès pour ${profile.displayName}:`, imageUrl);

      // Save to cache
      generatedImagesCache.current.set(profile.id, imageUrl);

      // Save to Supabase
      try {
        await saveProfileImageToDatabase(profile.id, imageUrl, true); // Assuming generated images are main images
        console.log(`✅ Image enregistrée dans la base de données pour ${profile.displayName}`);
      } catch (dbError) {
        console.error(`Échec de l'enregistrement de l'image dans la base de données pour ${profile.displayName}:`, dbError);
        // Continue anyway since we have the URL in memory/cache
      }

      if (showToast) {
        toast({
          title: 'Image générée',
          description: `Image de profil générée pour ${profile.displayName}`,
        });
      }

      return imageUrl; // Return the generated URL
    } catch (error) {
      console.error(`❌ Erreur lors de la génération d'image pour ${profile.displayName}:`, error);

      // Store the error
      setErrors(current => new Map([...current, [profile.id, String(error)]]));

      if (showToast) {
        toast({
          title: 'Échec de la génération',
          description: `Impossible de générer l'image pour ${profile.displayName}: ${error instanceof Error ? error.message : 'Erreur inconnue'}`,
          variant: 'destructive',
        });
      }

      // Rethrow error to allow caller to handle it if needed
      throw error;
    } finally {
      setIsGenerating(current => {
        const updated = new Map(current);
        updated.set(profile.id, false); // Ensure generating state is reset
        return updated;
      });
    }
  }, [isGenerating, toast]); // Dependency on isGenerating state and toast

  // Function to generate images for a batch of profiles
  const generateBatchImages = async (profileBatch: EscortProfile[]) => {
    // Generate images sequentially to avoid overwhelming the API
    for (const profile of profileBatch) {
      try {
        // Call the single profile generation function
        // The single function already handles caching and generation state
        await generateImageForProfile(profile, false); // Don't show individual toasts for batch
      } catch (error) {
        console.error(`Error in batch processing for profile ${profile.id}:`, error);
        // Show a toast for batch errors
        toast({
          title: 'Erreur de traitement par lot',
          description: `Impossible de générer l'image pour ${profile.displayName}. Vérifiez la console pour plus de détails.`,
          variant: 'destructive',
        });
        // Optionally, decide whether to continue the batch or stop on error
      }
    }
     toast({
        title: 'Traitement par lot terminé',
        description: `Tentative de génération d'images terminée pour le lot.`,
      });
  };


  return { isGenerating, errors, generateImageForProfile, generateBatchImages, generatedImagesCache };
}; 